import { EventEmitter } from 'events';
import { SessionJoinData, SessionReadyData, WebSocketCallServiceConfig } from './types';
import { OpusDecoder } from 'opus-decoder';
import { toast } from 'react-toastify';
import i18n from 'i18next';

import { CALLER_AUDIO_CAPTURE } from '@shared/constants/audioCapture';

import { initOpus, OpusAPI, OpusEncoder } from '../Opus';
import { normalizeToF32 } from './utils';
import { CallWsEvent, DEFAULT_OPUS_PACKAGE_CONFIG } from './consts';

/**
 * WebSocketCallService
 * - Captures mic audio via AudioWorklet (mono, 48kHz, 20ms, 960 samples)
 * - Encodes to Opus and streams frames over WebSocket
 * - Optionally decodes received Opus to PCM and plays via AudioWorklet
 * - Optional self-monitor: immediately decode and play locally what we encode
 */
export class WebSocketCallService extends EventEmitter {
  private ws: WebSocket | null = null;

  private serviceUrl: string;
  private token?: string;
  private campaignId: string;
  private customerName: string;

  private audioContext: AudioContext | null = null;
  private mediaStream: MediaStream | null = null;
  private captureNode: AudioWorkletNode | null = null;
  private playbackNode: AudioWorkletNode | null = null;
  private audioGainNode: GainNode | null = null;
  private mediaOutDest: MediaStreamAudioDestinationNode | null = null;
  private remoteAudioEl: HTMLAudioElement | null = null;
  private playbackClock = 0;
  private micReader: ReadableStreamDefaultReader | null = null;

  private opusApi?: OpusAPI;
  private opusEncoder?: OpusEncoder;
  private opusDecoder?: OpusDecoder;
  private isRecording = false;
  private isSessionReady = false;
  private callId: string | null = null;

  private selfMonitor = false; // enable local monitor (loopback)

  constructor(private config: WebSocketCallServiceConfig) {
    super();
    this.serviceUrl = config.serviceUrl;
    this.campaignId = config.campaignId;
    this.customerName = config.customerName ?? 'Anonymous';

    if (config.onConnectedToServer) this.on('connectedToServer', config.onConnectedToServer);
    if (config.onSessionReady) this.on('sessionReady', config.onSessionReady);
    if (config.onTranscriptReceived) this.on('transcriptReceived', config.onTranscriptReceived);
    if (config.onError) this.on('error', config.onError);
    if (config.onDisconnect) this.on('disconnect', config.onDisconnect);

    this.on('connectionReady', async (data: SessionJoinData) => {
      try {
        await this.joinSession(data);
      } catch (e) {
        this.emit('error', e);
      }
    });
  }

  attachOutputElement(el: HTMLAudioElement) {
    this.remoteAudioEl = el;
    if (this.mediaOutDest) {
      this.remoteAudioEl.srcObject = this.mediaOutDest.stream;
      this.remoteAudioEl.autoplay = true;
      this.remoteAudioEl.play().catch(() => {
        /* ignore */
      });
    }
  }
  /**
   * Connect to WebSocket backend and start session
   */
  async connect(): Promise<void> {
    this.token = localStorage.getItem('accessToken') ?? undefined;
    if (this.ws || !this.token) return;
    const url = `${this.serviceUrl}/v1/rtc-test`;
    this.ws = new WebSocket(url, ['bearer', this.token]);
    this.ws.binaryType = 'arraybuffer';

    this.ws.onopen = () => {
      console.log('Connected to WS');
    };

    this.ws.onmessage = (ev: MessageEvent) => this.handleServerMessage(ev);
    this.ws.onerror = (ev: Event) => this.emit('error', new Error('WebSocket error'));
    this.ws.onclose = () => {
      this.emit('disconnect');
      this.ws = null;
    };
  }

  /**
   * Send JSON over WS
   */
  private send(event: string, data: SessionJoinData) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify({ event, data }));
  }

  /**
   * Join ongoing session
   */
  private async joinSession(data: SessionJoinData): Promise<void> {
    this.send(CallWsEvent.SessionJoin, data);
  }

  /**
   * Handle all messages from server
   */
  private handleServerMessage(ev: MessageEvent) {
    const res = ev.data;
    if (res instanceof ArrayBuffer) {
      // fallback to Opus
      this.emit('audioReceived', res);
      return;
    }

    try {
      const msg = JSON.parse(res as string);

      const { event, data } = msg || {};
      switch (event) {
        case CallWsEvent.SessionReady: {
          const info: SessionReadyData = data;
          this.isSessionReady = true;
          this.callId = info.callId;
          this.emit('sessionReady', info);
          break;
        }
        case CallWsEvent.AgentTranscript: {
          this.emit('transcriptReceived', data);
          break;
        }
        case CallWsEvent.ConnectionReady: {
          this.emit('connectionReady', {
            campaignId: this.campaignId,
            customer: { name: this.customerName },
            includeTranscript: true,
          });
          break;
        }
        case CallWsEvent.SessionClose: {
          toast.info(i18n.t('agentPage.status.callEndedByBackend'), {
            autoClose: 5000,
          });
          this.endCall();
          break;
        }
        case CallWsEvent.Exception: {
          const errorMessage = data?.error.message ?? 'Server error';
          toast.error(errorMessage, { autoClose: 5000 });
          this.emit('error', new Error(errorMessage));
          break;
        }
        default:
          break;
      }
    } catch {}
  }

  /**
   * Initialize audio capture, encoder, decoders and playback worklets
   */
  async initializeAudioCapture(): Promise<void> {
    try {
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: DEFAULT_OPUS_PACKAGE_CONFIG.channels,
          sampleRate: DEFAULT_OPUS_PACKAGE_CONFIG.sampleRate,
          echoCancellation: true,
          autoGainControl: true,
          noiseSuppression: true,
          ...CALLER_AUDIO_CAPTURE,
        },
      });

      this.audioContext = new AudioContext({
        sampleRate: DEFAULT_OPUS_PACKAGE_CONFIG.sampleRate,
        latencyHint: 'interactive',
      });
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }

      this.mediaOutDest = this.audioContext.createMediaStreamDestination();

      this.audioGainNode = this.audioContext.createGain();
      this.audioGainNode.gain.value = 0.8;
      this.audioGainNode.connect(this.mediaOutDest);

      if (!this.remoteAudioEl) {
        this.remoteAudioEl = new Audio();
        this.remoteAudioEl.autoplay = true;
      }
      this.remoteAudioEl.srcObject = this.mediaOutDest.stream;
      this.remoteAudioEl.play().catch(() => {
        /* ignore autoplay gate */
      });

      this.opusApi = await initOpus();
      this.opusEncoder = this.opusApi.createEncoder(
        DEFAULT_OPUS_PACKAGE_CONFIG.sampleRate,
        DEFAULT_OPUS_PACKAGE_CONFIG.channels,
        DEFAULT_OPUS_PACKAGE_CONFIG.applicationVoip,
      );

      try {
        this.opusDecoder =
          (await (OpusDecoder as any)?.create?.({
            channels: DEFAULT_OPUS_PACKAGE_CONFIG.channels,
            sampleRate: DEFAULT_OPUS_PACKAGE_CONFIG.sampleRate,
          })) ??
          new (OpusDecoder as any)(
            DEFAULT_OPUS_PACKAGE_CONFIG.sampleRate,
            DEFAULT_OPUS_PACKAGE_CONFIG.channels,
          );
      } catch (error) {
        console.error('Failed to initialize Opus decoder:', error);
        this.opusDecoder = undefined;
      }

      await this.readMicLoop(this.mediaStream);

      this.on('audioReceived', async (opusFrame: ArrayBuffer) => {
        if (!this.opusDecoder) return;
        const u8 = new Uint8Array(opusFrame);
        const raw = this.decodeWithopusDecoder(u8);
        if (!raw) return;
        const f32 = normalizeToF32(raw);
        if (!f32 || !this.audioContext || !this.audioGainNode) return;
        this.playPcmF32(f32);
      });

      this.isRecording = true;
    } catch (error) {
      console.error('Failed to initialize audio capture:', error);
      throw error;
    }
  }

  private playPcmF32(f32: Float32Array): void {
    if (!this.audioContext || !this.audioGainNode) return;
    try {
      const ctx = this.audioContext;
      const buffer = ctx.createBuffer(
        DEFAULT_OPUS_PACKAGE_CONFIG.channels,
        f32.length,
        DEFAULT_OPUS_PACKAGE_CONFIG.sampleRate,
      );
      buffer.copyToChannel(f32 as Float32Array<ArrayBuffer>, 0, 0);

      const src = ctx.createBufferSource();
      src.buffer = buffer;
      src.connect(this.audioGainNode);

      const now = ctx.currentTime;
      if (this.playbackClock < now + 0.05) this.playbackClock = now + 0.05;
      src.start(this.playbackClock);
      this.playbackClock += buffer.duration;
    } catch (e) {
      console.warn('playPcmF32 failed', e);
    }
  }

  /**  read mic MediaStreamTrackProcessor (Chrome/Edge/Opera), fallback to ScriptProcessor */
  private async readMicLoop(stream: MediaStream): Promise<void> {
    const track = stream.getAudioTracks()[0];
    const wantRate = DEFAULT_OPUS_PACKAGE_CONFIG.sampleRate;

    const hasTrackProcessor = typeof (window as any).MediaStreamTrackProcessor === 'function';

    if (hasTrackProcessor) {
      const Processor = (window as any).MediaStreamTrackProcessor;
      const processor = new Processor({ track });
      this.micReader = processor.readable.getReader();

      const pump = async () => {
        if (!this.micReader) return;
        const { value, done } = await this.micReader.read();
        if (done || !value) return;

        // value: AudioData
        const audioData: any = value;
        try {
          const rate = audioData.sampleRate || wantRate;
          const frames = audioData.numberOfFrames;
          const tmp = new Float32Array(frames);

          audioData.copyTo(tmp, { planeIndex: 0, format: 'f32' });
          audioData.close();

          let f32 = tmp;
          if (rate !== wantRate) {
            const ratio = wantRate / rate;
            const outLen = Math.round(f32.length * ratio);
            const out = new Float32Array(outLen);
            for (let i = 0; i < outLen; i++) {
              out[i] = f32[Math.min(f32.length - 1, Math.round(i / ratio))] || 0;
            }
            f32 = out;
          }

          const pcm16 = new Int16Array(f32.length);
          for (let i = 0; i < f32.length; i++) {
            let s = f32[i];
            s = Math.max(-1, Math.min(1, s));
            pcm16[i] = (s * 32767) | 0;
          }

          if (this.opusEncoder) {
            const packet = this.opusEncoder.encode(pcm16);
            if (packet) {
              if (this.isSessionReady && this.ws && this.ws.readyState === WebSocket.OPEN) {
                this.sendAudioData(packet.buffer as ArrayBuffer);
              }

              if (this.selfMonitor && this.opusDecoder) {
                const raw = this.decodeWithopusDecoder(packet);
                const smF32 = raw ? normalizeToF32(raw) : null;
                if (smF32) this.playPcmF32(smF32);
              }
            }
          }
        } catch (_) {
          // ignore frame errors
        }

        pump();
      };
      pump();
    } else {
      if (!this.audioContext) return;
      const ctx = this.audioContext;
      const sourceNode = ctx.createMediaStreamSource(stream);
      const frameSize = DEFAULT_OPUS_PACKAGE_CONFIG.frameSize; // 960 @ 48kHz ~20ms
      const spn = ctx.createScriptProcessor(frameSize, 1, 1);
      sourceNode.connect(spn);

      spn.onaudioprocess = (ev) => {
        const input = ev.inputBuffer.getChannelData(0);
        const pcm16 = new Int16Array(input.length);
        for (let i = 0; i < input.length; i++) {
          let s = input[i];
          s = Math.max(-1, Math.min(1, s));
          pcm16[i] = (s * 32767) | 0;
        }

        if (this.opusEncoder) {
          const packet = this.opusEncoder.encode(pcm16);
          if (packet) {
            if (this.isSessionReady && this.ws && this.ws.readyState === WebSocket.OPEN) {
              this.sendAudioData(packet.buffer as ArrayBuffer);
            }
            if (this.selfMonitor && this.opusDecoder) {
              const raw = this.decodeWithopusDecoder(packet);
              const smF32 = raw ? normalizeToF32(raw) : null;
              if (smF32) this.playPcmF32(smF32);
            }
          }
        }
      };
    }
  }

  /**
   * Convert module source (string) to a blob URL for AudioWorklet
   */
  private toWorkletUrl(source: string) {
    const blob = new Blob([source], { type: 'application/javascript' });
    return URL.createObjectURL(blob);
  }

  /**
   * Send raw audio bytes over WebSocket
   */
  private sendAudioData(buffer: ArrayBuffer) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.ws.send(buffer);
  }

  /**
   * Play audio immediately - simple and direct
   */
  private playAudioImmediately(audioData: Float32Array): void {
    if (!this.audioContext) return;

    try {
      const audioBuffer = this.audioContext.createBuffer(
        DEFAULT_OPUS_PACKAGE_CONFIG.channels,
        audioData.length,
        DEFAULT_OPUS_PACKAGE_CONFIG.sampleRate,
      );
      const channelData = audioBuffer.getChannelData(0);
      channelData.set(audioData);

      const source = this.audioContext.createBufferSource();
      source.buffer = audioBuffer;

      // connect through gain node
      if (this.audioGainNode) {
        source.connect(this.audioGainNode);
      } else {
        source.connect(this.audioContext.destination);
      }

      source.start();
    } catch (error) {
      console.warn('Simple audio playback failed:', error);
    }
  }

  /**
   * Ensure audio context is in proper state for playback
   */
  private async ensureAudioContextReady(): Promise<void> {
    if (!this.audioContext) return;

    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }
  }

  /**
   * Decode Opus frame with the same lib the server uses and play it
   */
  private async decodeAndPlayFromServer(opusFrame: ArrayBuffer) {
    if (!this.opusDecoder) return;

    await this.ensureAudioContextReady();
    const u8 = new Uint8Array(opusFrame);

    const raw = this.decodeWithopusDecoder(u8);
    if (!raw) {
      console.warn('Opus decode returned null/empty');
      return;
    }

    const f32 = normalizeToF32(raw);
    if (f32) {
      if (this.playbackNode) {
        this.playbackNode.port.postMessage({ type: 'pcmF32', buffer: f32 }, [f32.buffer]);
      } else {
        // fallback to immediate playback if worklet not available
        this.playAudioImmediately(f32);
      }
    } else {
      console.warn('Failed to normalize audio frame');
    }
  }

  /**
   * Helper wrapper around 'opus-decoder' variants
   */
  private decodeWithopusDecoder(packet: Uint8Array) {
    const dec: any = this.opusDecoder;
    try {
      let result;
      result = dec.decodeFrame(packet);

      return result;
    } catch (e) {
      console.warn('opus-decoder decode failed', e);
      return null;
    }
  }

  /**
   * Start a call (connect WS + init audio path)
   */
  async startCall(): Promise<void> {
    await this.connect();
    await this.initializeAudioCapture();
  }

  /**
   * Stop audio capture and close WS
   */
  async endCall(): Promise<void> {
    this.isRecording = false;

    if (this.captureNode) {
      this.captureNode.port.onmessage = null as any;
      try {
        this.captureNode.disconnect();
      } catch {}
      this.captureNode = null;
    }

    if (this.playbackNode) {
      try {
        this.playbackNode.disconnect();
      } catch {}
      this.playbackNode = null;
    }

    if (this.audioGainNode) {
      try {
        this.audioGainNode.disconnect();
      } catch {}
      this.audioGainNode = null;
    }

    if (this.opusEncoder) {
      try {
        this.opusEncoder.destroy();
      } catch {}
      this.opusEncoder = undefined;
    }

    this.opusDecoder = undefined;

    if (this.audioContext) {
      try {
        await this.audioContext.close();
      } catch {}
      this.audioContext = null;
    }

    if (this.mediaStream) {
      try {
        this.mediaStream.getTracks().forEach((t) => t.stop());
      } catch {}
      this.mediaStream = null;
    }

    if (this.ws) {
      try {
        this.ws.close();
      } catch {}
      this.ws = null;
    }

    this.isSessionReady = false;
    this.callId = null;
  }

  /**
   * Set audio gain for smoother output
   */
  setAudioGain(gain: number): void {
    if (this.audioGainNode) {
      this.audioGainNode.gain.value = Math.max(0, Math.min(1, gain));
    }
  }

  /**
   * Destroy the service and remove listeners
   */
  destroy(): void {
    this.endCall();
    this.removeAllListeners();
  }
}
